// Bridge bill of quantities — Tools → Bridge Reports (docs/digitwin/Bridges Roadmap.md, Phase 5 / report 5).
//
// Volumes are OBM's own solid volumes (CifBridge SolidEntityAspect, never the triangles), grouped by
// material, then element type. Lengths / areas / counts come from OBM's own values too (pile length,
// girder length, barrier length, deck top area). Every row is a part from GET /api/bridges/.../quantities;
// this file only groups and sums. A part with no material in OBM is listed as such, never guessed.
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Table, Button, Spin, Alert } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

const MONO = "'DM Mono', monospace";
const ALL = "__all__";
const NOT_SET = "Not set in OBM";
const TYPE_ORDER = ["Cap", "Column", "Footing", "Concrete Pad", "Pile", "Cheek Wall", "Wingwall",
  "Bearing", "Beam Seat", "Grout Pad", "Girder", "Deck"];
const typeRank = (t) => { const i = TYPE_ORDER.indexOf(t); return i < 0 ? TYPE_ORDER.length : i; };
const byType = (a, b) => typeRank(a) - typeRank(b) || String(a).localeCompare(String(b));
const natural = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true });
const f3 = (v) => (v == null ? "—" : v.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 }));
const sum = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);
const groupBy = (rows, fn) => { const m = new Map(); for (const r of rows) { const k = fn(r); if (!m.has(k)) m.set(k, []); m.get(k).push(r); } return m; };

// Volumes: material → element type
function volumeTable(rows) {
  const out = [];
  for (const [material, mRows] of groupBy(rows, (r) => r.material || NOT_SET)) {
    const children = [...groupBy(mRows, (r) => r.type)].sort(([a], [b]) => byType(a, b)).map(([type, tRows]) => ({
      key: `${material}|${type}`, label: type, count: tRows.length,
      volume: sum(tRows, "volume"), surfaceArea: sum(tRows, "surfaceArea"),
      fromAssembly: tRows.some((r) => r.materialFrom === "assembly"),
    }));
    out.push({ key: material, label: material, missing: material === NOT_SET, count: mRows.length,
      volume: sum(mRows, "volume"), surfaceArea: sum(mRows, "surfaceArea"), children });
  }
  // Largest first; "not set" last
  return out.sort((a, b) => (a.missing - b.missing) || b.volume - a.volume);
}

// Lengths, areas and counts
function otherTable(rows) {
  const out = [];
  const add = (key, item, detail, count, quantity, unit) => out.push({ key, item, detail, count, quantity, unit });
  for (const [d, r] of [...groupBy(rows.filter((x) => x.type === "Pile"), (x) => x.diameter)].sort(([a], [b]) => a - b)) {
    add(`pile|${d}`, "Piles", d != null ? `Ø ${d.toFixed(3)} m` : "", r.length, sum(r, "length"), "m");
  }
  for (const [s, r] of [...groupBy(rows.filter((x) => x.type === "Girder"), (x) => x.section || "")].sort(([a], [b]) => natural(a, b))) {
    add(`girder|${s}`, "Girders", s, r.length, sum(r, "length"), "m");
  }
  const decks = rows.filter((x) => x.type === "Deck");
  if (decks.length) add("deck", "Deck", "top surface", decks.length, sum(decks, "topArea"), "m²");
  for (const [t, r] of [...groupBy(rows.filter((x) => x.headline === "Misc"), (x) => x.type)].sort(([a], [b]) => natural(a, b))) {
    add(`misc|${t}`, t, "", r.length, r.some((x) => x.length != null) ? sum(r, "length") : null, "m");
  }
  for (const t of ["Bearing", "Beam Seat", "Grout Pad"]) {
    const r = rows.filter((x) => x.type === t);
    if (r.length) add(`count|${t}`, `${t}s`, "", r.length, null, "");
  }
  return out;
}

export default function BridgeQuantitiesModal({ open, onClose, file, isMobile }) {
  const [data, setData] = useState(null); // { parts } | { error }
  const [bridge, setBridge] = useState(ALL);

  useEffect(() => { setData(null); setBridge(ALL); }, [file]);
  useEffect(() => {
    if (!open || data) return;
    fetch(`/api/bridges/${file.projectId}/${encodeURIComponent(file.fileName)}/quantities`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setData(d))
      .catch((e) => setData({ error: e.message }));
  }, [open, data, file]);

  const bridges = useMemo(() => [...new Set((data?.parts || []).map((p) => p.bridge))].sort(natural), [data]);
  const rows = useMemo(() => (data?.parts || []).filter((p) => bridge === ALL || p.bridge === bridge), [data, bridge]);
  const volumes = useMemo(() => volumeTable(rows), [rows]);
  const others = useMemo(() => otherTable(rows), [rows]);
  const missing = rows.filter((r) => !r.material);
  const scopeLabel = bridge === ALL ? `All bridges (${bridges.join(", ")})` : bridge;

  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheet = (aoa, numCols, widths) => {
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let R = 0; R <= range.e.r; R++) for (const C of numCols) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
        if (cell && cell.t === "n" && !Number.isInteger(cell.v)) cell.z = "#,##0.000";
      }
      ws["!cols"] = widths.map((wch) => ({ wch }));
      return ws;
    };
    const head = [[`Bridge bill of quantities — ${scopeLabel}`], ["Volumes and areas are OBM's own solid values (SolidEntity)"], []];
    const vol = [...head, ["Material", "Element", "Count", "Volume (m³)", "Surface area (m²)", "Material source"]];
    for (const m of volumes) {
      for (const c of m.children) vol.push([m.label, c.label, c.count, c.volume, c.surfaceArea, m.missing ? "—" : c.fromAssembly ? "assembly" : "part"]);
      vol.push([`${m.label} total`, "", m.count, m.volume, m.surfaceArea, ""]);
    }
    vol.push(["Total", "", rows.length, sum(rows, "volume"), sum(rows, "surfaceArea"), ""]);
    XLSX.utils.book_append_sheet(wb, sheet(vol, [3, 4], [44, 16, 8, 14, 18, 16]), "Volumes");
    const oth = [...head, ["Item", "Detail", "Count", "Quantity", "Unit"],
      ...others.map((o) => [o.item, o.detail, o.count, o.quantity ?? "", o.quantity != null ? o.unit : ""])];
    XLSX.utils.book_append_sheet(wb, sheet(oth, [3], [20, 22, 8, 14, 6]), "Lengths & counts");
    const parts = [...head, ["Bridge", "Headline", "Element", "Name", "Material", "Material source", "Volume (m³)", "Surface area (m²)", "Length (m)", "Top area (m²)", "Element id"],
      ...rows.map((r) => [r.bridge, r.headline, r.type, r.name, r.material || NOT_SET, r.materialFrom || "—", r.volume, r.surfaceArea, r.length ?? "", r.topArea ?? "", r.elementId])];
    XLSX.utils.book_append_sheet(wb, sheet(parts, [6, 7, 8, 9], [10, 15, 16, 26, 44, 15, 13, 16, 11, 13, 10]), "Parts");
    XLSX.writeFile(wb, `Bridge_BOQ_${bridge === ALL ? "All" : bridge}.xlsx`);
  };

  const num = (title, key, width = 130) => ({
    title, dataIndex: key, key, width, align: "right",
    render: (v) => <span style={{ fontFamily: MONO, fontVariantNumeric: "tabular-nums" }}>{typeof v === "number" ? f3(v) : (v ?? "—")}</span>,
  });
  const volumeColumns = [
    { title: "Material / element", dataIndex: "label", key: "label",
      render: (v, r) => (
        <span style={{ color: r.missing ? "#faad14" : undefined, fontWeight: r.children ? 600 : 400 }}>
          {v}{r.fromAssembly && <span style={{ fontFamily: MONO, fontSize: 10, color: "rgba(255,255,255,0.35)", marginLeft: 8 }}>from assembly</span>}
        </span>
      ) },
    { title: "Count", dataIndex: "count", key: "count", width: 80, align: "right", render: (v) => <span style={{ fontFamily: MONO }}>{v}</span> },
    num("Volume m³", "volume"),
    num("Surface area m²", "surfaceArea", 150),
  ];
  const otherColumns = [
    { title: "Item", dataIndex: "item", key: "item" },
    { title: "Detail", dataIndex: "detail", key: "detail", render: (v) => <span style={{ fontFamily: MONO, fontSize: 12 }}>{v}</span> },
    { title: "Count", dataIndex: "count", key: "count", width: 80, align: "right", render: (v) => <span style={{ fontFamily: MONO }}>{v}</span> },
    { title: "Quantity", dataIndex: "quantity", key: "quantity", width: 150, align: "right",
      render: (v, r) => <span style={{ fontFamily: MONO, fontVariantNumeric: "tabular-nums" }}>{v == null ? "—" : `${f3(v)} ${r.unit}`}</span> },
  ];
  const sectionTitle = (t) => <div style={{ fontFamily: MONO, fontSize: 11, color: "#4f9cf9", textTransform: "uppercase", letterSpacing: "0.08em", margin: "14px 0 6px" }}>{t}</div>;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={isMobile ? "96vw" : 960}
      title={<span style={{ fontFamily: MONO, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.12em" }}>Bridge Bill of Quantities</span>}
      footer={rows.length > 0 ? <Button icon={<DownloadOutlined />} onClick={downloadExcel} style={{ fontFamily: MONO, fontSize: 12 }}>Download Excel</Button> : null}
      styles={{ body: { maxHeight: "72vh", overflowY: "auto" } }}
    >
      {open && !data ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spin /></div>
      ) : data?.error ? (
        <Alert type="error" showIcon message={`Could not load quantities: ${data.error}`} />
      ) : bridges.length === 0 ? (
        <div style={{ fontFamily: MONO, fontSize: 12, color: "rgba(255,255,255,0.45)", padding: "12px 0" }}>No OpenBridge bridges in this model.</div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
            <Select value={bridge} onChange={setBridge} style={{ width: 170 }}
              options={[{ value: ALL, label: "All bridges" }, ...bridges.map((b) => ({ value: b, label: b }))]} />
            <span style={{ fontFamily: MONO, fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
              {rows.length} parts · {f3(sum(rows, "volume"))} m³ in total
            </span>
          </div>
          {missing.length > 0 && (
            <Alert style={{ marginTop: 10 }} type="warning" showIcon
              message={`${missing.length} part${missing.length > 1 ? "s have" : " has"} no material set in OBM: ${
                [...groupBy(missing, (r) => `${r.bridge} ${r.type}`)].map(([k, v]) => `${k} (${v.length})`).join(", ")}. Their volume is listed under "${NOT_SET}".`} />
          )}
          {sectionTitle("Volumes by material")}
          <Table size="small" columns={volumeColumns} dataSource={volumes} pagination={false}
            expandable={{ defaultExpandAllRows: true }}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><b>Total</b></Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right"><span style={{ fontFamily: MONO }}>{rows.length}</span></Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right"><b style={{ fontFamily: MONO }}>{f3(sum(rows, "volume"))}</b></Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right"><span style={{ fontFamily: MONO }}>{f3(sum(rows, "surfaceArea"))}</span></Table.Summary.Cell>
              </Table.Summary.Row>
            )} />
          {sectionTitle("Lengths, areas and counts")}
          <Table size="small" columns={otherColumns} dataSource={others} pagination={false} />
          <div style={{ fontFamily: MONO, fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 10, lineHeight: 1.6 }}>
            Volumes and surface areas are OBM's own solid values. Concrete pads, bearings, beam seats, grout pads and wingwall walls carry no material of their own; theirs comes from their assembly ("from assembly"). The Excel file adds a per-part sheet for checking.
          </div>
        </>
      )}
    </Modal>
  );
}
